import { useEffect, useMemo, useCallback } from "react";
import useCourtData from "./hooks/useCourtData";
import useCourtUI from "./hooks/useCourtUI";
import useCourtFiltering from "./hooks/useCourtFiltering";
import useSlotStatus from "./hooks/useSlotStatus";
import useBasketManager from "../FinalizePage/hooks/useBasketManager";

interface UseCourtGridOptions {
  externalDate?: Date;
  externalSelectedCourts?: string[];
}

export default function useCourtGrid(exerciseName?: string, options?: UseCourtGridOptions) {
  const uiState = useCourtUI(exerciseName);
  const { basket, getBasket } = useBasketManager();

  const selectedDate = options?.externalDate ?? uiState.selectedDate;
  const selectedCourts = options?.externalSelectedCourts ?? uiState.selectedCourts;

  useEffect(() => {
    getBasket();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exercisesInBasket = useMemo(() => {
    return basket
      .filter((item) => item.ticketAccess && item.ticketAccess.length > 0)
      .flatMap((item) => item.ticketAccess.map((access) => parseInt(access.exerciseIds)))
      .filter((id) => !isNaN(id));
  }, [basket]);

  const { courtGridData, loading, refetch } = useCourtData({
    selectedDate,
    selectedCourts,
    exerciseName,
  });

  const slotStatus = useSlotStatus({
    selectedDate,
    exercisesInBasket,
  });

  const filtering = useCourtFiltering({
    courtGridData,
    selectedCourts,
    isMobile: uiState.isMobile,
    currentCourtIndex: uiState.currentCourtIndex,
    exerciseName,
  });

  // Aktualizuj wybrane korty przy każdym ładowaniu danych
  useEffect(() => {
    if (courtGridData?.courts) {
      // Pobierz dostępne korty
      const availableCourtNames = courtGridData.courts.map((court) => court.courtName);

      // Filtruj tylko te korty, które użytkownik wcześniej zaznaczył i są dostępne w danym dniu
      const selectedCourts = uiState.persistentSelectedCourts.filter((courtName) =>
        availableCourtNames.includes(courtName)
      );

      // Ustaw wybrane korty
      uiState.setSelectedCourts(selectedCourts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courtGridData?.courts]);

  useEffect(() => {
    if (exerciseName) {
      const courts = exerciseName.split(";").map((name) => name.trim());
      uiState.setSelectedCourts(courts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseName]);

  const handleNextCourt = useCallback(() => {
    uiState.handleNextCourt(filtering.filteredCourts.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtering.filteredCourts.length]);

  const handleTicketAdded = useCallback((exerciseId: number) => {
    setTimeout(() => {
      getBasket();
    }, 100);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseReservationModalWithReset = useCallback(() => {
    uiState.handleCloseReservationModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    courtGridData,
    loading,
    selectedDate,
    selectedCourts,
    setSelectedDate: uiState.setSelectedDate,
    setSelectedCourts: uiState.setSelectedCourts,
    updateCourtFilters: uiState.updateCourtFilters,
    persistentSelectedCourts: uiState.persistentSelectedCourts,
    showPastHours: uiState.showPastHours,
    setShowPastHours: uiState.setShowPastHours,
    currentCourtIndex: uiState.currentCourtIndex,
    reservationModal: uiState.reservationModal,
    isMobile: uiState.isMobile,
    exercisesInBasket,
    // Data refresh
    refetchCourtData: refetch,
    // Filtering
    filteredCourts: filtering.filteredCourts,
    displayedCourts: filtering.displayedCourts,
    uniqueHours: filtering.uniqueHours,
    findSlot: filtering.findSlot,
    // Slot status
    getSlotStatus: slotStatus.getSlotStatus,
    isHourPastWithBuffer: slotStatus.isHourPastWithBuffer,
    visibleHours: slotStatus.visibleHours,
    hasPastHours: slotStatus.hasPastHours,
    // Handlers
    handleSlotClick: uiState.handleSlotClick,
    handleCloseReservationModal: handleCloseReservationModalWithReset,
    handlePreviousCourt: uiState.handlePreviousCourt,
    handleNextCourt,
    handleSetCourtIndex: uiState.handleSetCourtIndex,
    handleTicketAdded,
  };
}
